<template>
    <div v-if="!displayModalChoixSite" class="container">
        <div class="title-container">
            <span>
                {{ $t('devis.title') }}
                <ReglePastille cle="devis.definition" />
            </span>
        </div>
        <div class="content-container">
            <div class="filter-container">
                <div v-if="!isUniqueSite" class="filter-site-container">
                    {{ $t('intervention.non-preventive.filter') }}
                    <ButtonBlue
                        :disabled="tableData.length === 0 && isLoading"
                        :on-click="selectionnerSite"
                        :text="$t('bouton.choix.site')"
                        class="button-confirm btn"
                    />
                </div>
                <Filter
                    :filter="this.filter"
                    :statusList="this.statusList"
                    @addFilterStatus="this.addFilterStatus"
                    @clearFilter="this.clearFilter"
                    @removeFilterStatus="this.removeFilterStatus"
                    @search="search"
                    @setDevisId="this.setDevisId"
                    @setDevisPubliDateFrom="this.setDevisPubliDateFrom"
                    @setDevisPubliDateTo="this.setDevisPubliDateTo"
                    @setNumFactures="this.setNumFactures"
                    @setNumero="this.setNumero"
                    @statusToggleSelectAll="this.statusToggleSelectAll"
                />
            </div>
            <div class="dataTable">
                <div>
                    <h2>
                        {{ this.count }}
                        {{ $t('devis.dataTable.title', this.count > 1 ? 2 : 1) }}
                        <ReglePastille cle="suiviDevis.portee" />
                    </h2>
                    <BorderedIconButton
                        v-if="!isExportDisabled"
                        :imgUrl="iconeXls"
                        :title="$t('list.export')"
                        @click="exportXls"
                    />
                </div>
                <LoaderComponent v-if="tableData.length === 0 && isLoading" class="loader" />
                <div v-else ref="content" class="datatable-container" @scroll="handleScroll">
                    <DataTable
                        :count="+count"
                        :isLoading="isLoading"
                        :tableData="this.tableData"
                        :tableHeader="this.tableHeader"
                        @select="goToPage"
                        @sort="doSort"
                    />
                </div>
            </div>
            <WaitingModal v-if="isExportXlsLoading" />
        </div>
    </div>
    <ModalChoixSite
        v-else
        :is-multi-site="true"
        :inclure-termines="true"
        :selected-site="null"
        :selected-sites="filter.siteList"
        @cancel="modalChoixSiteCancel"
        @valider="validerSite"
    />
</template>

<script lang="ts">
import DataTable from '@/components/ui-components/DataTable.vue';
import { SortType, sortValue } from '@/models/SortType';
import { defineComponent, onMounted, ref } from 'vue';
import Filter from '@/modules/Common/components/Filter.vue';
import { DEVIS } from '../store/mutation-types';
import { DevisActions } from '../store/action-types';
import { Devis, EtatDevis } from '@/models/Devis';
import SiteListService, { SiteSearchDTO } from '@/modules/SiteList/service/SiteListService';
import currencyFormatter from 'currency-formatter';
import { statusName } from '@/components/ui-components/StatusTag.vue';
import { Site } from '@/models/Site';
import { TableHeader } from '@/models/TableHeader';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import router from '@/router';
import { store } from '@/plugins/store';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';
import ModalChoixSite from '@/modules/Common/components/ModalChoixSite.vue';
import ButtonBlue from '@/components/ui-components/ButtonBlue.vue';
import ReglePastille from '@/components/regles/ReglePastille.vue';

interface Data {
    siteList: any[];
    statusList: string[];
    filter: {
        siteList: any[];
        statusList: string[];
        devisId: string;
        numero: string;
        numFactures: string;
        devisPubliDateFrom: string;
        devisPubliDateTo: string;
    };
    siteListTotal: any[];
    debounce: number;
    searchSite: string;
    displayModalChoixSite: boolean;
    isUniqueSite: boolean;
}

export default defineComponent({
    name: 'ListDevisPage',
    components: { Filter, DataTable, LoaderComponent, BorderedIconButton, WaitingModal, ButtonBlue, ModalChoixSite, ReglePastille },
    data(): Data {
        return {
            siteList: [],
            statusList: [],
            filter: {
                siteList: [],
                statusList: [],
                devisId: '',
                numero: '',
                numFactures: '',
                devisPubliDateFrom: '',
                devisPubliDateTo: '',
            },
            siteListTotal: [],
            debounce: 0,
            searchSite: '',
            displayModalChoixSite: false,
            isUniqueSite: false,
        };
    },
    beforeMount() {
        this.filter.siteList = this.filterSite;
        SiteListService.getSiteList(new SiteSearchDTO(0, '', true, true)).then(res => {
            if (res.data.length === 1) {
                this.isUniqueSite = true;
                this.displayModalChoixSite = false;
            }
            this.siteList = res.data.map(
                ({ id, chantierId, description }: { id: string; chantierId: number; description: string }) => ({
                    id,
                    chantierId,
                    description,
                })
            );
            this.siteListTotal = this.siteList;
        });

        this.statusList = Object.values(EtatDevis);
        this.filter.statusList = this.filterStatus.length > 0 ? this.filterStatus : this.statusList;
        this.$store.commit(DEVIS.SET_FILTER_STATUS, this.filter.statusList);

        this.filter.devisId = this.filterDevisId;
        this.filter.devisPubliDateFrom = this.filterDateDebut;
        this.filter.devisPubliDateTo = this.filterDateFin;

        if (!this.displayModalChoixSite) {
            this.getDevisData();
        }
    },
    setup() {
        const content = ref<Element>();

        onMounted(() => {
            content.value;
        });

        return { content };
    },
    mounted() {
        this.content?.scrollTo({ top: this.scroll });
    },
    methods: {
        search() {
            this.$store.commit(DEVIS.RESET_ITEMS);
            this.doFilter();
        },
        getDevisData(page = 0) {
            page === 0 && this.content?.scrollTo({ top: 0 });

            this.$store.dispatch(DevisActions.GET_DEVIS, {
                page,
                sort: this.sort && sortValue(this.sort.sortData, this.sort.sortStatus),
                numDevis: this.filterDevisId !== '' ? this.filterDevisId : undefined,
                chantierIds:
                    this.filterSite?.length === this.siteListTotal?.length
                        ? []
                        : this.filterSite?.map(x => x.chantierId),
                numFacture: this.filterDevisNumFactures !== '' ? this.filterDevisNumFactures : undefined,
                numCommande: this.filterDevisNumero !== '' ? this.filterDevisNumero : undefined,
                etatDevis: this.filterStatus?.length === this.statusList?.length ? [] : this.filterStatus,
                dateDebut: this.filter.devisPubliDateFrom !== '' ? this.filter.devisPubliDateFrom : undefined,
                dateFin: this.filter.devisPubliDateTo !== '' ? this.filter.devisPubliDateTo : undefined,
            });
        },
        exportXls() {
            store.dispatch(DevisActions.EXPORT_XLS, {
                sort: this.sort && sortValue(this.sort.sortData, this.sort.sortStatus),
                numDevis: this.filterDevisId !== '' ? this.filterDevisId : undefined,
                chantierIds:
                    this.filterSite?.length === this.siteListTotal?.length
                        ? []
                        : this.filterSite?.map(x => x.chantierId),
                numFacture: this.filterDevisNumFactures !== '' ? this.filterDevisNumFactures : undefined,
                numCommande: this.filterDevisNumero !== '' ? this.filterDevisNumero : undefined,
                etatDevis: this.filterStatus?.length === this.statusList?.length ? [] : this.filterStatus,
                dateDebut: this.filter.devisPubliDateFrom !== '' ? this.filter.devisPubliDateFrom : undefined,
                dateFin: this.filter.devisPubliDateTo !== '' ? this.filter.devisPubliDateTo : undefined,
            });
        },
        doFilter() {
            this.$store.commit(DEVIS.SET_FILTER_SITE, this.filter.siteList);
            this.$store.commit(DEVIS.SET_FILTER_STATUS, this.filter.statusList);
            this.$store.commit(DEVIS.SET_FILTER_DEVIS_ID, this.filter.devisId);
            this.$store.commit(DEVIS.SET_FILTER_DEVIS_NUM_FACTURE, this.filter.numFactures);
            this.$store.commit(DEVIS.SET_FILTER_DATE_DEBUT, this.filter.devisPubliDateFrom);
            this.$store.commit(DEVIS.SET_FILTER_DATE_FIN, this.filter.devisPubliDateTo);

            this.$store.commit(DEVIS.SET_FILTER_DEVIS_NUMERO_COMMANDE, this.filter.numero);

            this.getDevisData();
        },
        doSort(sort: { sortData: string; sortStatus: SortType }) {
            const { sortData, sortStatus } = sort;
            this.$store.commit(DEVIS.SET_SORT, { sortData, sortStatus });
            this.$store.commit(DEVIS.RESET_ITEMS);
            this.getDevisData();
        },
        setDevisId(value: string): void {
            this.filter.devisId = value;
        },
        setDevisPubliDateFrom(value: string): void {
            this.filter.devisPubliDateFrom = value;
        },
        setDevisPubliDateTo(value: string): void {
            this.filter.devisPubliDateTo = value;
        },
        setNumero(value: string): void {
            this.filter.numero = value;
        },
        setNumFactures(value: string): void {
            this.filter.numFactures = value;
        },
        clearFilter() {
            this.filter.devisId = '';
            this.filter.numero = '';
            this.filter.numFactures = '';
            this.filter.devisPubliDateFrom = '';
            this.filter.devisPubliDateTo = '';
            this.filter.siteList = this.siteListTotal ?? [];
            this.filter.statusList = this.statusList ?? [];

            this.doFilter();
        },
        handleScroll() {
            const top = this.content?.scrollTop ?? 0;
            const max = this.content?.scrollHeight ?? 0;
            const height = this.content?.clientHeight ?? 0;
            this.$store.commit(DEVIS.SET_SCROLL, top);
            if (max - top <= height * 1.5 && !this.isLoading && this.page + 1 < this.numberOfPages) {
                this.$store.commit(DEVIS.SET_PAGE, this.page + 1);
                this.getDevisData(this.page);
            }
        },
        addFilterStatus(status: string) {
            this.filter.statusList = [...this.filter.statusList, status];
        },
        removeFilterStatus(status: string) {
            this.filter.statusList = this.filter.statusList.filter(statusData => statusData !== status);
        },
        statusToggleSelectAll() {
            this.filter.statusList = this.filter.statusList.length === this.statusList.length ? [] : this.statusList;
        },
        goToPage(item: { devisId: { pageName: string; label: string; value: number } }) {
            router.push({ name: item.devisId.pageName, params: { id: item.devisId.value, value: item.devisId.label } });
        },
        validerSite(sites: Site[]): void {
            this.siteList = sites;
            this.displayModalChoixSite = false;
            this.$store.commit(DEVIS.SET_FILTER_SITE, sites);
            this.$store.commit(DEVIS.RESET_ITEMS);
            this.getDevisData();
        },
        modalChoixSiteCancel(): void {
            this.siteList = this.siteListTotal;
            this.displayModalChoixSite = false;
            if (this.tableData.length === 0 && !this.isLoading) this.getDevisData();
        },
        selectionnerSite(): void {
            this.displayModalChoixSite = true;
        },
    },
    computed: {
        tableData() {
            return this.$store.getters.getDevisItems.map(
                ({
                    numero,
                    numCommande,
                    version,
                    objet,
                    prix,
                    monnaie,
                    montantFacture,
                    numFactures,
                    etatDevis,
                    chiffrageId,
                    dateChiffrage,
                    demandeDevisId,
                }: Devis) => ({
                    devisId: { pageName: 'detailDevisPage', value: chiffrageId, label: numero + '-' + version },
                    objet,
                    numero: numCommande,
                    demandeDevisId: demandeDevisId != null ? demandeDevisId : '',
                    prix: currencyFormatter.format(prix, { code: monnaie }),
                    montantFacture: montantFacture ? currencyFormatter.format(montantFacture, { code: monnaie }) : '',
                    numFactures:
                        numFactures?.reduce((list, item, index) => {
                            return `${list}${index === 0 ? '' : ' '}${item}`;
                        }, '') ?? '',
                    dateChiffrage: new Date(dateChiffrage).toLocaleDateString(),
                    statut: statusName(etatDevis),
                })
            );
        },
        sort(): {
            sortData: string;
            sortStatus: SortType;
        } {
            return this.$store.getters.getDevisSort;
        },
        tableHeader(): TableHeader[] {
            return this.$store.getters.getDevisTableHeader;
        },
        page() {
            return this.$store.getters.getDevisPage;
        },
        count() {
            return this.$store.getters.getDevisCount;
        },
        numberOfPages() {
            return this.$store.getters.getDevisNbOfPages;
        },
        scroll() {
            return this.$store.getters.getDevisScroll;
        },
        isLoading() {
            return this.$store.getters.getDevisIsLoading;
        },
        filterSite(): any[] {
            return this.$store.getters.getDevisFilterSite;
        },
        filterStatus(): string[] {
            return this.$store.getters.getDevisFilterStatus;
        },
        filterDevisId() {
            return this.$store.getters.getDevisFilterDevisId;
        },
        filterDevisNumero() {
            return this.$store.getters.getDevisFilterNumero;
        },
        filterDevisNumFactures() {
            return this.$store.getters.getDevisFilterNumFactures;
        },
        filterDateDebut() {
            return this.$store.getters.getDevisFilterDateDebut;
        },
        filterDateFin() {
            return this.$store.getters.getDevisFilterDateFin;
        },
        filterMine(): boolean {
            return this.$store.getters.getDevisFilterMine;
        },
        iconeXls() {
            return require('@/assets/img/xls.svg');
        },
        isExportDisabled(): boolean {
            return (
                this.filter.siteList?.length === 0 ||
                this.filter.statusList?.length === 0 ||
                this.tableData.length === 0
            );
        },
        isExportXlsLoading(): boolean {
            return store.getters.getIsExportXlsLoading;
        },
    },
    watch: {
        tableData() {
            this.filter.statusList = this.filterStatus.length > 0 ? this.filterStatus : this.statusList;
            this.filter.siteList = this.filterSite.length > 0 ? this.filterSite : this.siteList;
        },
        filterStatus() {
            this.content?.scrollTo({ top: 0 });
        },
        filterSite() {
            this.content?.scrollTo({ top: 0 });
        },
    },
});
</script>

<style lang="scss" scoped>
@import '@/theme/pages-recherche.scss';

.datatable-container {
    max-height: 60vh;
}

@media screen and (max-width: 1631px) {
    .datatable-container {
        max-height: calc(100vh - 360px);
    }
}

@media screen and (max-width: 1531px) {
    .datatable-container {
        max-height: calc(100vh - 430px);
    }
}

@media screen and (max-width: 970px) {
    .datatable-container {
        max-height: calc(100vh - 500px);
    }
}
:deep(.containerBtn) {
    height: 28px;
}
:deep(.icon) {
    height: 20px;
}
.filter-container {
    display: flex;
    padding: 8px 16px;
    background: white;
    border-radius: 6px;
    margin-bottom: 8px;
}
.filter-site-container {
    flex-direction: column;
    width: 15%;
}
.filter {
    width: 100%;
}
.button-confirm {
    right: 16px;
}
.btn {
    width: 100%;
    border-radius: 5px;
    cursor: pointer;
    height: 40px;
    margin-top: 20px;
}
:deep(.filter) {
    margin-bottom: 0;
    padding: 0 16px;
}
</style>
